import { Avatar, Button, cx, useToast } from '@cartly/ui';
import {
  Building2,
  Check,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Plus,
  ShieldCheck,
  ShieldAlert,
  Sun,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { api, errorMessage } from '../api/client';
import { CURRENT_PHASE, NAV, type NavItem } from '../app/nav';
import { Logo } from '../components/Logo';
import { useActiveSession, useSession } from '../app/session';
import { useTheme, type ThemePreference } from '../app/theme';

const COLLAPSE_KEY = 'cartly.sidebar.collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Closes a dropdown when clicking outside it or pressing Escape. */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
}

/** A nav item with sub-pages: a toggle row that expands to its links. */
function NavGroup({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const { can } = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const key = `cartly.nav.open.${item.path}`;
  const inside = pathname === item.path || pathname.startsWith(`${item.path}/`);
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  // Landing on one of the group's pages (a link, a bookmark) opens it.
  const [wasInside, setWasInside] = useState(inside);
  if (inside !== wasInside) {
    setWasInside(inside);
    if (inside) setOpen(true);
  }
  const Icon = item.icon;
  const children = (item.children ?? []).filter((c) => !c.permission || can(c.permission));
  const expanded = open && !collapsed;

  return (
    <div>
      <button
        type="button"
        className={cx('sidebar__link', 'sidebar__group-toggle', inside && 'sidebar__link--active')}
        title={collapsed ? item.label : undefined}
        aria-expanded={expanded}
        onClick={() => {
          // The icon-only sidebar has no room for sub-links: go to the group's first page.
          if (collapsed) {
            navigate(item.path);
            onNavigate();
            return;
          }
          const next = !open;
          setOpen(next);
          try {
            localStorage.setItem(key, next ? '1' : '0');
          } catch {
            /* remembering the state is optional */
          }
        }}
      >
        <Icon className="sidebar__link-icon" size={18} />
        <span className="sidebar__link-label">{item.label}</span>
        <ChevronRight
          size={14}
          className={cx('sidebar__chevron', expanded && 'sidebar__chevron--open')}
        />
      </button>
      {expanded && (
        <div className="sidebar__sub">
          {children.map((c) => (
            <NavLink
              key={c.path}
              to={c.path}
              end
              onClick={onNavigate}
              className={({ isActive }) =>
                cx('sidebar__sublink', isActive && 'sidebar__sublink--active')
              }
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function Sidebar({
  collapsed,
  onToggle,
  onNavigate,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const { can } = useSession();
  const { user } = useActiveSession();
  return (
    <aside className="sidebar" aria-label="Main navigation">
      <div className="sidebar__brand">
        <Logo variant="wordmark" surface="dark" className="sidebar__wordmark" />
        <Logo variant="mark" surface="dark" className="sidebar__mark" />
      </div>
      <nav className="sidebar__nav">
        {NAV.map((section) => {
          const items = section.items.filter((i) => !i.permission || can(i.permission));
          if (items.length === 0) return null;
          return (
            <div key={section.title ?? 'main'}>
              {section.title && <div className="sidebar__section">{section.title}</div>}
              {items.map((item) => {
                if (item.children) {
                  return (
                    <NavGroup
                      key={item.path}
                      item={item}
                      collapsed={collapsed}
                      onNavigate={onNavigate}
                    />
                  );
                }
                const Icon = item.icon;
                const available = item.phase <= CURRENT_PHASE || item.live === true;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    title={collapsed ? item.label : undefined}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cx(
                        'sidebar__link',
                        isActive && 'sidebar__link--active',
                        !available && 'sidebar__link--muted',
                      )
                    }
                  >
                    <Icon className="sidebar__link-icon" size={18} />
                    <span className="sidebar__link-label">{item.label}</span>
                    {!available && <span className="sidebar__pill">Soon</span>}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
        {user.isPlatformAdmin && (
          <div>
            <div className="sidebar__section">Platform</div>
            <NavLink
              to="/super-admin"
              title={collapsed ? 'Super Admin' : undefined}
              onClick={onNavigate}
              className={({ isActive }) =>
                cx('sidebar__link', isActive && 'sidebar__link--active')
              }
            >
              <ShieldAlert className="sidebar__link-icon" size={18} />
              <span className="sidebar__link-label">Super Admin</span>
            </NavLink>
          </div>
        )}
      </nav>
      <div className="sidebar__footer">
        <button
          type="button"
          className="sidebar__collapse"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          <span className="sidebar__collapse-label">Collapse</span>
        </button>
      </div>
    </aside>
  );
}

function OrgSwitcher() {
  const session = useActiveSession();
  const { refresh } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const active = session.activeTenant;

  const switchTo = async (tenantId: string) => {
    setOpen(false);
    if (tenantId === active?.id) return;
    try {
      await api.post('/tenants/switch', { tenantId });
      await refresh();
      navigate('/');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="menu" ref={ref}>
      <button
        type="button"
        className="topbar__org"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Building2 size={16} />
        <span className="topbar__org-name">{active?.name ?? 'Select organization'}</span>
        <ChevronsUpDown size={14} />
      </button>
      {open && (
        <div className="menu__panel menu__panel--left" role="menu">
          <div className="menu__section">Organizations</div>
          {session.tenants.map((t) => (
            <button
              key={t.id}
              type="button"
              role="menuitem"
              className={cx('menu__item', t.id === active?.id && 'menu__item--active')}
              onClick={() => void switchTo(t.id)}
            >
              <span className="row-between menu__grow">
                <span>{t.name}</span>
                {t.id === active?.id && <Check size={14} />}
              </span>
            </button>
          ))}
          <div className="menu__divider" />
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setOpen(false);
              navigate('/onboarding');
            }}
          >
            <Plus size={14} /> New organization
          </button>
        </div>
      )}
    </div>
  );
}

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: ReactNode }[] = [
  { value: 'light', label: 'Light', icon: <Sun size={14} /> },
  { value: 'dark', label: 'Dark', icon: <Moon size={14} /> },
  { value: 'system', label: 'System', icon: <Monitor size={14} /> },
];

function UserMenu() {
  const session = useActiveSession();
  const { logout } = useSession();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));

  return (
    <div className="menu" ref={ref}>
      <button
        type="button"
        className="topbar__user"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
      >
        <Avatar name={session.user.name} />
      </button>
      {open && (
        <div className="menu__panel" role="menu">
          <div className="menu__section">
            <div className="text-strong">{session.user.name}</div>
            <div>{session.user.email}</div>
          </div>
          <div className="menu__divider" />
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setOpen(false);
              navigate('/settings/security');
            }}
          >
            <ShieldCheck size={14} /> Security
          </button>
          {session.user.isPlatformAdmin && (
            <button
              type="button"
              role="menuitem"
              className="menu__item"
              onClick={() => {
                setOpen(false);
                navigate('/super-admin');
              }}
            >
              <ShieldAlert size={14} /> Super Admin
            </button>
          )}
          <div className="menu__divider" />
          <div className="menu__section">Theme</div>
          {THEME_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="menuitemradio"
              aria-checked={theme === o.value}
              className={cx('menu__item', theme === o.value && 'menu__item--active')}
              onClick={() => setTheme(o.value)}
            >
              {o.icon} {o.label}
            </button>
          ))}
          <div className="menu__divider" />
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => void logout().then(() => navigate('/login'))}
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function AppShell() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, v ? '0' : '1');
      } catch {
        // Preference not persisted.
      }
      return !v;
    });
  };

  return (
    <div
      className={cx(
        'app-shell',
        collapsed && 'app-shell--collapsed',
        drawerOpen && 'app-shell--drawer-open',
      )}
    >
      <Sidebar
        collapsed={collapsed}
        onToggle={toggleCollapsed}
        onNavigate={() => setDrawerOpen(false)}
      />
      <div className="sidebar-backdrop" onClick={() => setDrawerOpen(false)} />
      <div className="app-main">
        <header className="topbar">
          <Button
            variant="ghost"
            iconOnly
            className="topbar__menu-button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
            icon={<Menu size={18} />}
          />
          <OrgSwitcher />
          <div className="topbar__spacer" />
          <UserMenu />
        </header>
        <main className="app-content" id="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
